import { VisualSearch } from "./VisualSearch.js";
import { CasePhoto } from "./CasePhoto.js";
import { Suspense, lazy, useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router";
import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData } from "@tanstack/react-query";
import { AlertTriangle, Info, LayoutGrid, ListFilter, Network, Search, SearchX, X } from "lucide-react";
import type { SearchPage as SearchPageData, SearchRequest } from "../../../shared/contracts.js";
import { api, errorMessage, isApiError } from "../../api.js";
import { useMe } from "../../components/AppShell.js";
import { Button } from "../../components/ui/Button.js";
import { Switch } from "../../components/ui/Form.js";
import { Callout, EmptyState, ErrorState, Spinner } from "../../components/ui/States.js";
import { cx } from "../../lib/cx.js";
import { fmtInt, fmtMs } from "../../lib/format.js";
import { useDocumentTitle } from "../../lib/hooks.js";
import { liveKeywords, useLiveSearch } from "../../lib/live.js";
import { rememberSearch, saveSearchScroll, searchScroll } from "../../lib/last-search.js";
import {
  loadContext, toSearchContext, type ContextDraft,
} from "../../lib/search-context.js";
import { qk, searchViewQuery, type SearchView } from "../../queries.js";
import { AiSummary } from "./AiSummary.js";
import { VehicleLookupHint } from "./VehicleLookupHint.js";
import { AdvancedForm } from "./AdvancedForm.js";
import {
  EMPTY_ADVANCED, daysFromIncident, draftFromParsed, isAdvancedParsed, toAdvanced, type AdvancedDraft, type AdvancedErrors,
} from "./advanced.js";
import { LivePanel, ManualLinks } from "./LivePanel.js";
import { IntelProgress, IntelResults, IntelSuggestions, suggestedCount, useIntel } from "./IntelPanel.js";
import { Tabs, TabPanel } from "../../components/ui/Tabs.js";
import { useToast } from "../../components/ui/Toast.js";
import { ParsedSummary } from "./ParsedSummary.js";
import { ResultCard, ResultCardSkeleton } from "./ResultCard.js";

// Cytoscape pesa ~400 KB: el mapa se descarga solo al abrir la pestaña.
const EvidenceMap = lazy(() => import("./EvidenceMap.js"));

const EXAMPLES = [
  "BBCL12", "toyota yaris 2018", "Juan Pérez", "hilux 2020 maipú", "vin: 9BWZZZ377VT004251", "rut: 12.345.678-5",
];

function resultsQuery(id: string) {
  return {
    queryKey: qk.searchResults(id),
    queryFn: ({ pageParam, signal }: { pageParam: string | null; signal: AbortSignal }) =>
      api.searches.results(id, pageParam, signal),
    initialPageParam: null as string | null,
    getNextPageParam: (last: SearchPageData) => last.nextCursor,
    // Cada página es una consulta auditada: no se vuelve a pedir sola.
    staleTime: Infinity,
    // Se conservan al ir a un aviso y volver (sin re-pedir ni re-auditar).
    gcTime: 60 * 60_000,
  };
}

/**
 * Lo del caso para buscar dentro de una cuenta o grupo: marca y modelo como se
 * escribieron ("Suzuki Swift"), o la patente si no hay vehículo.
 */
function caseTermsOf(p: SearchView["parsed"]): string {
  const typed = (h: { value: string; span?: { start: number; end: number } } | undefined): string =>
    h ? (h.span ? p.raw.slice(h.span.start, h.span.end) : h.value) : "";
  const vehicle = [typed(p.make), typed(p.model)].filter(Boolean).join(" ").replace(/\s+/g, " ").trim();
  return vehicle || (p.plates.find((x) => x.strength === "exact" || x.strength === "joined")?.plate ?? "");
}

/** Cuerpo para repetir una búsqueda (p. ej. "Ampliar" en internet) a partir de su vista. */
function requestFromView(v: SearchView, extra: Partial<SearchRequest>): SearchRequest {
  return {
    q: v.queryText,
    filters: v.filters,
    context: {
      purpose: v.context.purpose,
      ...(v.context.purposeDetail ? { purposeDetail: v.context.purposeDetail } : {}),
      ...(v.context.caseRef ? { caseRef: v.context.caseRef } : {}),
      ...(v.context.subjectRole ? { subjectRole: v.context.subjectRole } : {}),
    },
    ...extra,
  };
}

export function SearchPage() {
  useDocumentTitle("Buscar");
  const me = useMe();
  const qc = useQueryClient();
  const [params, setParams] = useSearchParams();
  const searchId = params.get("s");

  const liveAllowed = me.institution ? me.institution.liveSearchEnabled : true;
  // Sin barra de contexto: la finalidad se registra igual (historial y auditoría) con
  // "Prevención de fraude" por defecto, o la que quedó guardada en esta pestaña.
  const [ctx] = useState<ContextDraft>(() => {
    const c = loadContext();
    return c.purpose && (c.purpose !== "other" || c.purposeDetail.trim().length >= 3) ? c : { ...c, purpose: "fraud_prevention" };
  });
  const [q, setQ] = useState("");
  const [qError, setQError] = useState<string | null>(null);
  const [live, setLive] = useState(me.institution?.liveSearchEnabled ?? false);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [adv, setAdv] = useState<AdvancedDraft>(EMPTY_ADVANCED);
  const [advErrors, setAdvErrors] = useState<AdvancedErrors>({});
  const mode: "unificada" | "avanzada" = params.get("modo") === "avanzada" ? "avanzada" : "unificada";
  /** Parámetros de la URL conservando el modo (solo ids y el modo: nunca datos buscados). */
  const urlFor = (s: string | null, m = mode): Record<string, string> => ({
    ...(m === "avanzada" ? { modo: "avanzada" } : {}),
    ...(s ? { s } : {}),
  });
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsHeadingRef = useRef<HTMLHeadingElement>(null);

  const view = useQuery({ ...searchViewQuery(searchId ?? ""), enabled: searchId !== null });
  const results = useInfiniteQuery({ ...resultsQuery(searchId ?? ""), enabled: searchId !== null });

  // Al reabrir desde el historial o al volver desde un aviso, la caja y el formulario
  // avanzado muestran lo que se buscó (el texto viene del servidor o de la caché; nunca de la URL).
  const hydratedFor = useRef<string | null>(null);
  useEffect(() => {
    const v = view.data;
    if (!v || hydratedFor.current === v.id) return;
    hydratedFor.current = v.id;
    const wasAdvanced = v.advanced ?? isAdvancedParsed(v.parsed);
    setQ(wasAdvanced ? "" : v.queryText);
    setAdv(draftFromParsed(v.parsed));
    if (wasAdvanced && v.reopened) setParams(urlFor(v.id, "avanzada"), { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [view.data]);
  useEffect(() => {
    if (searchId) rememberSearch(`/buscar?${new URLSearchParams(params).toString()}`);
  }, [searchId, params]);

  const create = useMutation({
    mutationFn: api.searches.create,
    onSuccess: (res, req) => {
      const v: SearchView = {
        id: res.searchId,
        queryText: req.q,
        parsed: res.parsed,
        filters: req.filters ?? {},
        context: {
          purpose: req.context.purpose,
          purposeDetail: req.context.purposeDetail ?? null,
          caseRef: req.context.caseRef ?? null,
          subjectRole: req.context.subjectRole ?? null,
        },
        createdAt: new Date().toISOString(),
        user: { fullName: me.user.fullName, email: me.user.email },
        broad: res.broad,
        totalEstimate: res.totalEstimate,
        tookMs: res.tookMs,
        live: res.live,
        manualLinks: res.manualLinks,
        reopened: false,
        advanced: !!req.advanced,
        ...(res.relaxed ? { relaxed: res.relaxed } : {}),
      };
      hydratedFor.current = res.searchId;
      qc.setQueryData(qk.searchView(res.searchId), v);
      qc.setQueryData<InfiniteData<SearchPageData, string | null>>(qk.searchResults(res.searchId), {
        pages: [{ results: res.results, nextCursor: res.nextCursor }],
        pageParams: [null],
      });
      setParams(urlFor(res.searchId, req.advanced ? "avanzada" : "unificada"));
      // Lectores de pantalla: el foco pasa al encabezado de resultados.
      requestAnimationFrame(() => resultsHeadingRef.current?.focus());
    },
    onError: (err) => {
      if (isApiError(err, 400)) {
        const fe = err.fieldErrors;
        if (fe.q) {
          setQError(fe.q);
          return;
        }
      }
      setSubmitError(err);
    },
  });

  function runSearch(text: string, extra: Partial<SearchRequest> = {}) {
    setSubmitError(null);
    setQError(null);
    const c = toSearchContext(ctx);
    const trimmed = text.trim();
    if (!c.ok) {
      setSubmitError(new Error("Finalidad de la búsqueda inválida; recargue la página."));
      return;
    }
    if (!trimmed) {
      setQError("Escribe una patente, un vehículo o un nombre.");
      inputRef.current?.focus();
      return;
    }
    create.mutate({
      q: trimmed.slice(0, 200),
      context: c.value,
      live: liveAllowed && live,
      ...extra,
    });
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    runSearch(q);
  }

  function runAdvanced() {
    const r = toAdvanced(adv);
    if (!r.ok) {
      setAdvErrors(r.errors);
      return;
    }
    setAdvErrors({});
    runSearch(r.label, { advanced: r.value });
  }

  function clearSearch() {
    setQ("");
    setQError(null);
    setSubmitError(null);
    setParams(urlFor(null));
    inputRef.current?.focus();
  }

  const v = view.data;
  const liveOn = v ? v.live.status !== "disabled" || Boolean(v.live.reason) : false;
  // Análisis profundo: corre solo en segundo plano; cada actualización trae los
  // resultados nuevos que encontró (expansión y pistas) al panel de internet.
  const intel = useIntel(v?.id ?? null, liveOn);
  const [liveRestart, setLiveRestart] = useState(0);
  // Tras «Actualizar» se sigue por sondeo (el stream original ya terminó).
  const liveInit = v?.live && liveRestart > 0 ? { ...v.live, status: "running" as const, streamUrl: null } : (v?.live ?? null);
  const liveState = useLiveSearch(v?.id ?? null, liveInit, v?.reopened || liveRestart > 0 ? "poll" : "stream", intel.dataUpdatedAt, liveRestart);
  const toastLive = useToast();
  const refreshLive = useMutation({
    mutationFn: () => api.searches.refreshLive(v!.id),
    onSuccess: () => {
      setLiveRestart((n) => n + 1);
      toastLive.success("Actualizando la búsqueda en internet sin usar respuestas guardadas.");
    },
    onError: (e: Error) => toastLive.error(errorMessage(e)),
  });
  const [sideTab, setSideTab] = useState<"resultados" | "recomendadas">("resultados");
  useEffect(() => { setSideTab("resultados"); }, [v?.id]);
  const [layout, setLayout] = useState<"lista" | "mapa">("lista");
  const hits = useMemo(() => results.data?.pages.flatMap((p) => p.results) ?? [], [results.data]);
  const nextPageError = results.isFetchNextPageError ? results.error : null;
  // Misma foto en otros avisos, para todos los avisos del caso a la vez (tope 60).
  const caseIds = hits.slice(0, 60).map((h) => h.listingId);
  const photoMatches = useQuery({
    queryKey: ["searches", searchId, "photo-matches", caseIds.join(",")],
    queryFn: ({ signal }) => api.listings.photoMatchSummary(caseIds, signal),
    enabled: caseIds.length > 0,
    staleTime: 10 * 60_000,
  });
  const mapInput = useMemo(() => (v ? {
    parsed: v.parsed, hits, web: liveState.results, intel: intel.data ?? null, photoMatches: photoMatches.data?.byListing ?? null,
  } : null), [v, hits, liveState.results, intel.data, photoMatches.data]);

  // Al volver desde un aviso, los resultados quedan donde se dejaron (no arriba).
  // Se guarda la posición solo después de restaurarla, para no pisarla al montar.
  const scrollReadyFor = useRef<string | null>(null);
  const resultsReady = searchId !== null && !results.isPending && hits.length > 0;
  useEffect(() => {
    if (!resultsReady || !searchId || scrollReadyFor.current === searchId) return;
    const y = searchScroll(searchId);
    if (y !== null) requestAnimationFrame(() => window.scrollTo(0, y));
    scrollReadyFor.current = searchId;
  }, [resultsReady, searchId]);
  useEffect(() => {
    if (!searchId) return;
    let raf = 0;
    const onScroll = (): void => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        if (scrollReadyFor.current === searchId) saveSearchScroll(searchId, window.scrollY);
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
    };
  }, [searchId]);
  const liveRequested = v ? v.live.status !== "disabled" || Boolean(v.live.reason) : false;

  return (
    <div className="space-y-4">
      <h1 className="sr-only">Buscar</h1>

      <div role="tablist" aria-label="Tipo de búsqueda" className="flex gap-1 border-b border-border">
        {([["unificada", "Búsqueda unificada", <Search key="i" className="size-4" aria-hidden="true" />],
          ["avanzada", "Búsqueda avanzada", <ListFilter key="i" className="size-4" aria-hidden="true" />]] as const).map(([m, label, icon]) => (
          <button
            key={m}
            type="button"
            role="tab"
            aria-selected={mode === m}
            onClick={() => setParams(urlFor(searchId, m), { replace: true })}
            className={cx(
              "-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-[13.5px] font-medium",
              mode === m ? "border-accent text-accent" : "border-transparent text-muted hover:text-fg",
            )}
          >
            {icon}
            {label}
          </button>
        ))}
      </div>

      {mode === "avanzada" ? (
        <AdvancedForm value={adv} onChange={(n) => { setAdv(n); if (Object.keys(advErrors).length) setAdvErrors({}); }}
          errors={advErrors} onSubmit={runAdvanced} loading={create.isPending} />
      ) : (
      <form onSubmit={onSubmit} role="search" aria-label="Búsqueda unificada" className="space-y-2" noValidate>
        <div className="flex flex-col gap-2 sm:flex-row">
          <div className="relative min-w-0 flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-4 size-5 -translate-y-1/2 text-subtle" aria-hidden="true" />
            <label htmlFor="q" className="sr-only">
              Patente, VIN, RUT, teléfono, marca, modelo, año o nombre
            </label>
            <input
              id="q"
              ref={inputRef}
              type="search"
              value={q}
              onChange={(e) => {
                setQ(e.target.value);
                if (qError) setQError(null);
              }}
              maxLength={200}
              autoComplete="off"
              spellCheck={false}
              enterKeyHint="search"
              aria-invalid={qError ? true : undefined}
              aria-describedby={qError ? "q-error" : "q-hint"}
              placeholder="Patente, VIN, RUT, teléfono, vehículo o nombre — ej: BBCL12 · toyota yaris 2018 rojo · Juan Pérez"
              className={cx(
                "h-13 w-full rounded-lg border border-border-strong bg-surface pr-11 pl-12 text-base text-fg shadow-card",
                "placeholder:text-subtle hover:border-subtle focus-visible:border-accent focus-visible:outline-2 focus-visible:outline-offset-0",
                "aria-[invalid=true]:border-danger [&::-webkit-search-cancel-button]:hidden",
              )}
            />
            {q && (
              <button
                type="button"
                onClick={clearSearch}
                aria-label="Limpiar búsqueda"
                className="absolute top-1/2 right-2 grid size-8 -translate-y-1/2 place-items-center rounded-md text-subtle hover:bg-surface-2 hover:text-fg"
              >
                <X className="size-4" aria-hidden="true" />
              </button>
            )}
          </div>
          <Button type="submit" variant="primary" size="lg" className="h-13 px-6" loading={create.isPending}>
            Buscar
          </Button>
        </div>
        {qError && (
          <p id="q-error" role="alert" className="text-[12.5px] font-medium text-danger">
            {qError}
          </p>
        )}
        <p><VehicleLookupHint example /></p>
      </form>
      )}
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
          <Switch
            checked={liveAllowed && live}
            onChange={setLive}
            disabled={!liveAllowed}
            label="Buscar también en internet (en vivo)"
            description={
              liveAllowed
                ? "Envía la patente o el nombre a un buscador externo; queda registrado."
                : "Tu institución no tiene habilitada la búsqueda en internet."
            }
          />
          <p id="q-hint" className="text-[12px] text-subtle sm:ml-auto">
            Enter para buscar. La búsqueda nunca se guarda en la dirección de la página.
          </p>
        </div>

      {mode === "unificada" && (
        <>
          <CasePhoto context={() => { const c = toSearchContext(ctx); return c.ok ? c.value : null; }} />
          <VisualSearch context={() => { const c = toSearchContext(ctx); return c.ok ? c.value : null; }} />
        </>
      )}

      {submitError !== null && (
        <Callout
          tone={isApiError(submitError, 429) ? "warning" : "danger"}
          icon={<AlertTriangle />}
          title={isApiError(submitError, 429) ? "Límite alcanzado" : "No se pudo buscar"}
        >
          {errorMessage(submitError)}
        </Callout>
      )}

      {searchId === null ? (
        mode === "avanzada" ? null : <Welcome
          onExample={(ex) => {
            // Solo completa la caja: la búsqueda (y su registro en auditoría) la decide el analista.
            setQ(ex);
            inputRef.current?.focus();
          }}
        />
      ) : view.isError ? (
        <ErrorState error={view.error} title="No se pudo abrir la búsqueda" onRetry={() => void view.refetch()} />
      ) : (
        // En el mapa, el panel de internet pasa abajo: el mapa necesita todo el ancho.
        <div className={cx("grid grid-cols-1 items-start gap-4",
          layout === "lista" && "lg:grid-cols-[minmax(0,1fr)_340px] xl:grid-cols-[minmax(0,1fr)_380px]")}>
          <section aria-labelledby="results-title" className="min-w-0 space-y-3">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
              <h2 id="results-title" ref={resultsHeadingRef} tabIndex={-1} className="text-[15px] font-semibold text-fg outline-none">
                {results.isPending || !v ? "Buscando…" : resultsTitle(hits.length, v, results.hasNextPage)}
              </h2>
              {v?.tookMs !== null && v?.tookMs !== undefined && (
                <span className="text-[12px] text-subtle">en {fmtMs(v.tookMs)}</span>
              )}
              {v && (
                <div role="group" aria-label="Vista de los resultados" className="ml-auto inline-flex rounded-md border border-border p-0.5">
                  {([["lista", "Lista", <LayoutGrid key="i" className="size-3.5" aria-hidden="true" />],
                    ["mapa", "Mapa", <Network key="i" className="size-3.5" aria-hidden="true" />]] as const).map(([k, label, icon]) => (
                    <button key={k} type="button" aria-pressed={layout === k} onClick={() => setLayout(k)}
                      className={cx("inline-flex items-center gap-1 rounded px-2 py-1 text-[12.5px] font-medium",
                        layout === k ? "bg-accent text-accent-fg" : "text-muted hover:text-fg")}>
                      {icon}{label}
                    </button>
                  ))}
                </div>
              )}
            </div>
            {v && <ParsedSummary parsed={v.parsed} />}
            {v && !results.isPending && (
              <AiSummary searchId={v.id} liveDone={!["queued", "running"].includes(liveState.status)} />
            )}
            {v?.broad && (
              <Callout tone="info" icon={<Info />} title="Búsqueda muy amplia">
                Se muestran los más recientes; refina con año, modelo o nombre.
              </Callout>
            )}
            {v?.relaxed && (
              <Callout tone="warning" icon={<Info />} title="Ningún aviso cumple todos los datos">
                Se muestran los que coinciden con el resto. Se dejó de lado: <strong>{relaxedText(v.relaxed.missing)}</strong>.
                Revise si corresponden al caso.
                {v.relaxed.missing.some((m) => m === "plate" || m === "vin") && (
                  <> La patente puede estar tapada o no haberse leído bien en las fotos: revise las imágenes de cada aviso.</>
                )}
              </Callout>
            )}

            {layout === "mapa" && mapInput ? (
              <Suspense fallback={<div className="flex items-center gap-2 py-6 text-muted" role="status"><Spinner /> Cargando el mapa…</div>}>
                <EvidenceMap input={mapInput} />
              </Suspense>
            ) : results.isPending ? (
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3">
                {[0, 1, 2, 3, 4, 5].map((i) => <ResultCardSkeleton key={i} />)}
              </div>
            ) : results.isError && !results.data ? (
              <ErrorState error={results.error} title="No se pudieron cargar los resultados" onRetry={() => void results.refetch()} />
            ) : hits.length === 0 ? (
              <EmptyState
                icon={<SearchX />}
                title="Sin resultados en el índice"
                description="Revisa la patente o el nombre, quita filtros o prueba con menos palabras (por ejemplo, solo marca y modelo)."
              />
            ) : (
              <>
                <ul className="grid grid-cols-1 gap-3 sm:grid-cols-2 2xl:grid-cols-3" aria-label="Avisos encontrados">
                  {hits.map((h) => (
                    <li key={h.listingId} className="flex">
                      <ResultCard
                        hit={h}
                        incidentDays={v?.parsed.incident ? daysFromIncident(h.seenAt, v.parsed.incident.date) : null}
                        photoMatches={photoMatches.data?.byListing[h.listingId]}
                      />
                    </li>
                  ))}
                </ul>
                {results.hasNextPage && (
                  <div className="flex justify-center pt-2">
                    <Button onClick={() => void results.fetchNextPage()} loading={results.isFetchingNextPage}>
                      Cargar más
                    </Button>
                  </div>
                )}
                {nextPageError !== null && (
                  <p role="alert" className="text-center text-[13px] text-danger">
                    No se pudieron cargar más resultados: {errorMessage(nextPageError)}
                  </p>
                )}
              </>
            )}
          </section>

          <aside aria-label="Resultados de internet" className={cx("min-w-0",
            layout === "lista" && "lg:sticky lg:top-[4.5rem] lg:max-h-[calc(100dvh-5.5rem)] lg:overflow-y-auto lg:overscroll-contain lg:rounded-xl")}>
            {v && (
              <Tabs
                idPrefix="side"
                label="Panel de internet"
                value={sideTab}
                onChange={setSideTab}
                className="mb-3"
                items={[
                  { value: "resultados", label: "Resultados" },
                  { value: "recomendadas", label: "Búsquedas recomendadas", count: suggestedCount(intel.data) + v.manualLinks.length || null },
                ]}
              />
            )}
            {v && sideTab === "recomendadas" ? (
              <TabPanel idPrefix="side" value="recomendadas">
                <div className="overflow-hidden rounded-xl border border-border bg-surface shadow-card">
                  {liveRequested && <IntelSuggestions searchId={v.id} v={intel.data} results={liveState.results} />}
                  <ManualLinks links={v.manualLinks} />
                  {!liveRequested && v.manualLinks.length === 0 && (
                    <p className="px-4 py-4 text-[13px] text-muted">Sin búsquedas recomendadas para esta consulta.</p>
                  )}
                </div>
              </TabPanel>
            ) : v ? (
              <TabPanel idPrefix="side" value="resultados">
              {liveRequested && <IntelProgress v={intel.data} />}
              <LivePanel
                showManualLinks={false}
                intel={intel.data}
                onRefresh={liveAllowed ? () => refreshLive.mutate() : undefined}
                refreshing={refreshLive.isPending || liveState.status === "running" || liveState.status === "queued"}
                live={liveState}
                requested={liveRequested}
                disabledReason={v.live.status === "disabled" ? (v.live.reason ?? null) : null}
                manualLinks={v.manualLinks}
                area={v.parsed.location && !v.parsed.location.soft
                  ? [v.parsed.location.comuna, v.parsed.location.region].filter((x): x is string => !!x)
                  : []}
                keywords={liveKeywords(v.parsed.freeText)}
                caseTerms={caseTermsOf(v.parsed)}
                pivotBusy={create.isPending}
                onPivot={liveAllowed ? (p) => {
                  const c = toSearchContext(ctx);
                  create.mutate(requestFromView(v, { q: p.label.slice(0, 200), live: true, pivot: p.pivot, ...(c.ok ? { context: c.value } : {}) }));
                } : undefined}
              />
              {liveRequested && <IntelResults v={intel.data} />}
              </TabPanel>
            ) : (
              <div className="flex items-center gap-2 px-2 py-6 text-muted" role="status">
                <Spinner /> Preparando…
              </div>
            )}
          </aside>
        </div>
      )}
    </div>
  );
}

function resultsTitle(shown: number, v: SearchView, more: boolean): string {
  if (shown === 0) return "Sin resultados";
  const total = v.totalEstimate;
  if (total !== null && total > shown) {
    return `${fmtInt(shown)} de ${total >= 10_000 ? "más de 10.000" : `≈ ${fmtInt(total)}`} resultados`;
  }
  return `${fmtInt(shown)}${more ? "+" : ""} ${shown === 1 ? "resultado" : "resultados"}`;
}

const RELAXED_LABELS: Record<string, string> = {
  plate: "patente", vin: "VIN", rut: "RUT", phone: "teléfono", email: "correo", person: "nombre",
  text: "palabras clave", location: "comuna/región", year: "año", color: "color",
};

function relaxedText(missing: string[]): string {
  const xs = missing.map((m) => RELAXED_LABELS[m] ?? m);
  return xs.length <= 1 ? (xs[0] ?? "algunos datos") : `${xs.slice(0, -1).join(", ")} y ${xs.at(-1)}`;
}

function Welcome({ onExample }: { onExample: (q: string) => void }) {
  return (
    <div className="rounded-xl border border-dashed border-border-strong bg-surface px-4 py-10 text-center">
      <Search className="mx-auto mb-3 size-8 text-subtle" aria-hidden="true" />
      <h2 className="text-[16px] font-semibold text-fg">Busca en todos los avisos recolectados</h2>
      <p className="mx-auto mt-1 max-w-xl text-muted">
        Una sola caja para todo lo que tengas, junto o por separado: patente, VIN o n° de chasis/motor, RUT, teléfono,
        marca, modelo, año, color, comuna o nombre. El sistema reconoce solo qué es cada dato. Se busca en el texto
        completo de cada aviso, en las patentes leídas de las fotos y, si lo activas, en internet; arriba de los
        resultados verás un resumen con IA que cruza lo encontrado.
      </p>
      <p className="mx-auto mt-2 max-w-xl text-[12.5px] text-subtle">
        Si un dato es ambiguo, puedes etiquetarlo: <code className="font-mono">vin:</code>{" "}
        <code className="font-mono">patente:</code> <code className="font-mono">rut:</code>{" "}
        <code className="font-mono">tel:</code> <code className="font-mono">nombre:</code>{" "}
        <code className="font-mono">color:</code> <code className="font-mono">comuna:</code>
      </p>
      <p className="mt-4 text-[12.5px] text-subtle">Ejemplos (se copian a la caja):</p>
      <div className="mt-2 flex flex-wrap justify-center gap-2">
        {EXAMPLES.map((ex) => (
          <button
            key={ex}
            type="button"
            onClick={() => onExample(ex)}
            className="rounded-full border border-border-strong bg-surface-2 px-3 py-1 font-mono text-[13px] text-fg hover:border-accent hover:text-accent"
          >
            {ex}
          </button>
        ))}
      </div>
    </div>
  );
}
